import { FastumTicketWithIssues } from "@boark/shared/types/fastum-ticket-with-issues";
import { useSearchParams } from "react-router-dom";

import { Button } from "../../components/button";
import { H1 } from "../../components/h1";
import { Column, Filter, Table } from "../../components/table";
import {
  useGetFastumTickets,
  useImportFastumTickets,
} from "../../queries/fastum";
import { useLocale } from "../../stores/locale";
import { formatDate } from "../../utils/date";

export function IssueFastumList() {
  const { locale: t } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: tickets, isLoading: isTicketsLoading } =
    useGetFastumTickets();
  const importFastumTicketsMutation = useImportFastumTickets();

  function updateSearchParams(params: Record<string, string>) {
    setSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      ...params,
    });
  }

  return (
    <div className="w-full min-h-full overflow-x-auto">
      <div className="flex justify-between items-center">
        <H1>{t.issue.fastumTickets}</H1>
        <Button
          loading={importFastumTicketsMutation.isPending}
          onClick={() => {
            importFastumTicketsMutation.mutate();
          }}
        >
          {t.issue.fastumSync}
        </Button>
      </div>
      <Table
        actions={(row: FastumTicketWithIssues) => [
          {
            label: t.issue.fastumOpenExternal,
            href: row.link,
            target: "_blank",
          },
          ...row.issues.map((issue) => ({
            label: `#${issue.identifier} — ${issue.title}`,
            route: `/admin/issue/list/${issue.identifier}`,
          })),
        ]}
        data={tickets || []}
        filterBy={JSON.parse(searchParams.get("filterBy") || "[]") as Filter[]}
        loading={isTicketsLoading}
        locale={{ yes: t.yes, no: t.no, actions: t.action.actions, empty: t.empty }}
        sortBy={searchParams.get("sortBy") || "createdAt"}
        sortByDirection={
          (searchParams.get("sortByDirection") as "asc" | "desc") || "desc"
        }
        onFilter={(value: Filter[]) => {
          updateSearchParams({
            filterBy: JSON.stringify(value),
          });
        }}
        onSort={(field, direction) => {
          updateSearchParams({
            sortBy: field,
            sortByDirection: direction,
          });
        }}
      >
        <Column field="externalId" filterableBy="text">
          {t.issue.fastumTicketId}
        </Column>
        <Column field="title" filterableBy="text">
          {t.issue.title}
        </Column>
        <Column
          body={(row: FastumTicketWithIssues) =>
            row.linkedToIssue ? t.yes : t.no
          }
          field="linkedToIssue"
          filterableBy="select"
          filterOptions={() => [
            { label: t.yes, value: true },
            { label: t.no, value: false },
          ]}
        >
          {t.issue.fastumLinkedToIssue}
        </Column>
        <Column
          field="status"
          filterableBy="select"
          filterOptions={(data: FastumTicketWithIssues[]) =>
            [
              ...new Set(data.map((ticket) => ticket.status).filter(Boolean)),
            ].map((status) => ({
              label: status,
              value: status,
            }))
          }
        >
          {t.status}
        </Column>
        <Column
          body={(row: FastumTicketWithIssues) => row.type?.text ?? ""}
          field="type.text"
          filterableBy="text"
        >
          {t.issue.fastumType}
        </Column>
        <Column
          body={(row: FastumTicketWithIssues) => row.class?.text ?? ""}
          field="class.text"
          filterableBy="text"
        >
          {t.issue.fastumClass}
        </Column>
        <Column
          body={(row: FastumTicketWithIssues) => formatDate(row.createdAt)}
          field="createdAt"
          filterableBy="date"
        >
          {t.issue.createdAt}
        </Column>
        <Column
          body={(row: FastumTicketWithIssues) =>
            row.actionAt ? formatDate(row.actionAt) : "-"
          }
          field="actionAt"
          filterableBy="date"
        >
          {t.issue.fastumActionAt}
        </Column>
        <Column
          body={(row: FastumTicketWithIssues) =>
            row.updateAt ? formatDate(row.updateAt) : "-"
          }
          field="updateAt"
          filterableBy="date"
        >
          {t.issue.updatedAt}
        </Column>
      </Table>
    </div>
  );
}
